import { useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { fetchPostTruelayerBankAuthenticationUrl } from '@iwoca/lapi-client/edge';
import { Modal } from '@iwoca/orion';
import { useLingui } from '@lingui/react/macro';

import BankConnectionView from './BankConnectionView';
import BankListView from './BankListView';
import styles from './TrueLayerModal.module.css';
import { useContextStateKey } from '../../../../hooks/useContextStateKey';

export type TTrueLayerBank = {
  bank_id: string;
  bank_name: string;
  logo_url: string;
};

enum trueLayerModal {
  NONE = 'none',
  BANK_LIST = 'bankList',
  BANK_CONNECTION = 'bankConnection',
}

const TrueLayerModal = ({
  isOpen,
  handleClose,
  bankList,
}: {
  isOpen: boolean;
  handleClose: () => void;
  bankList: TTrueLayerBank[];
}) => {
  const [banks, setBanks] = useState<TTrueLayerBank[]>(bankList);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBank, setSelectedBank] = useState<TTrueLayerBank | null>(null);
  const [view, setView] = useState<trueLayerModal>(trueLayerModal.BANK_LIST);
  const [trueLayerBankUrl, setTrueLayerBankUrl] = useState('');
  const [bankUrlError, setBankUrlError] = useState(false);

  const stateKey = useContextStateKey();
  const { t } = useLingui();

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (value === '') return setBanks(bankList);

    const filteredBanks = bankList.filter((bank) =>
      bank.bank_name.toLowerCase().includes(value.toLowerCase()),
    );

    setBanks(filteredBanks);
  };

  const handleBankSelect = async (bank: TTrueLayerBank) => {
    if (!stateKey) return;

    tracking.ampli.trueLayerBankSelection({
      bankId: bank.bank_id,
      bankName: bank.bank_name,
    });

    setSelectedBank(bank);
    setView(trueLayerModal.BANK_CONNECTION);

    try {
      const response = await fetchPostTruelayerBankAuthenticationUrl({
        stateKey,
        providerId: bank.bank_id,
      });

      if (response) {
        setTrueLayerBankUrl(response?.data?.bank_url);
      }
    } catch {
      setBankUrlError(true);
    }
  };

  const handleBackClick = () => {
    setTrueLayerBankUrl('');
    setBankUrlError(false);
    setView(trueLayerModal.BANK_LIST);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      closeIcon={true}
      headerBar={true}
      title={t`Connect your business account`}
      className={`${styles.modalContainer} fs-mask dd-privacy-mask`}
    >
      {view === trueLayerModal.BANK_LIST && (
        <BankListView
          banks={banks}
          onSearch={handleSearch}
          searchTerm={searchTerm}
          onBankSelect={handleBankSelect}
        />
      )}
      {view === trueLayerModal.BANK_CONNECTION && selectedBank && (
        <BankConnectionView
          onClickBack={handleBackClick}
          onClose={handleClose}
          selectedBank={selectedBank}
          trueLayerBankUrl={trueLayerBankUrl}
          bankUrlError={bankUrlError}
        />
      )}
    </Modal>
  );
};

export default TrueLayerModal;
